export interface MarketHistoryDataPoint {
  price: number;
  date: string;
}

/** Standard solution Security type codes.*/
export enum SecurityTypeCode {
  EQUITY = "E", //Equity
  ETF = "CE", //Exchange-traded funds (ETFs)
  PRIVATE_EQUITY = "PE", //Private Equity
  DEBT_INSTRUMENT = "D", //Debt instruments
  COLLECTIVE_INVESTMENT_VEHICLE = "C", //Collective investment vehicles
  CURRENCY = "TC", //Currencies
}

export enum SecurityTypeName {
  CBL = "CBL Funds",
  OTHER = "Collective investment vehicles",
}

/**
 * Security tags indicating whether a security can be bought or sold
 * in units and/or trade amount.
 */
export enum SecurityTradeType {
  buyUnits = "Trade type:Buy units",
  buyTradeAmount = "Trade type:Buy trade amount",
  sellUnits = "Trade type:Sell units",
  sellTradeAmount = "Trade type:Sell trade amount",
}

export enum SecurityClassType1 {
  COMPLEX = "Complex",
  NON_COMPLEX = "Non-complex",
}

export interface SecurityDetailsPosition {
  id: number;
  name: string;
  namesAsMap: Record<string, string>;
  securityCode: string;
  blockSize?: number | undefined;
  isinCode: string;
  coupon?: number | undefined;
  maturityDate?: string | null;
  url: string;
  url2: string;
  classType1: {
    value: string;
    __typename: string;
  }
  currency: {
    securityCode: string;
    amountDecimalCount: number;
  };
  latestMarketData?: {
    price: number;
    date: string;
  };
  type: {
    id: number;
    code: SecurityTypeCode;
    namesAsMap: Record<string, string>;
    name: string;
  };
  fxRate: number;
  // misnamed on backend, should be tagsAsList
  tagsAsSet: string[];
  documents: {
    fileName: string;
    identifier: string;
    mimeType: string;
  }[];
  amountDecimalCount: number;
  managementFee: number;
  managementFeePercentage: number;
  minTradeAmount: number;
  profile?: {
    attribute?: {
      attributeKey?: string;
      stringValue?: string;
    }
  },
  country?: {
    id?: number;
    code: string;
    name?: string;
  };
  securityClassType1?: SecurityClassType1 | null;
}

export interface SecurityDetailsByCodeQuery {
  securities: SecurityDetailsPosition[];
}

export interface SecurityDetailsQuery {
  security: SecurityDetailsPosition;
}

export interface SecurityMarketDataHistory {
  marketDataHistory: MarketHistoryDataPoint[];
}

export interface SecurityMarketDataHistoryQuery {
  security: SecurityMarketDataHistory;
}

export interface HoldingPosition {
  security: {
    id: number;
  };
  amount: number;
  purchaseTradeAmount: number;
  marketValue: number;
  marketFxRate: number;
}

export interface PortfolioHoldingDetailsQuery {
  portfolio: {
    portfolioReport: {
      holdingPositions: HoldingPosition[];
    };
  };
}

export interface ContactHoldingDetailsQuery {
  contact: {
    id: number;
    portfolios: {
      id: number;
      portfolioReport: {
        holdingPositions: HoldingPosition[];
      };
    }[];
  };
}

export interface AnalyticsSecurityTypeData {
  code: string;
  name: string;
  firstAnalysis: {
    marketValue: number;
    marketTradeAmount: number;
    tradeAmount: number;
    shareOfTotal: number;
  } | null;
}

export interface AnalyticsSecurityData {
  security: {
    id: number;
    isinCode: string;
    countryCode: string;
    currencyCode: string;
    tagsAsList: string[];
    securityTypeCode: string;
    securityCode: string;
  };
  code: string;
  name: string;
  firstAnalysis: {
    marketValue: number;
    marketTradeAmount: number;
    tradeAmount: number;
    taxesWithProfitEffect: number;
    amount: number;
    accruedInterest: number;
    purchaseTradeAmount: number;
    marketFxRate: number;
    purchaseFxRate: number;
    subPortfolioReportItems: {
      purchaseAccruedInterest: number;
    }[];
  } | null;
}

export interface AnalyticsSecurityTypeDataWithSecurityData
  extends AnalyticsSecurityTypeData {
  securities: AnalyticsSecurityData[];
}

export interface ContactHoldingsFromAnalyticsQuery {
  contact: {
    id: number;
    analytics: {
      contact: {
        firstAnalysis: {
          marketValue: number;
          tradeAmount: number;
        } | null;
        securityTypes: AnalyticsSecurityTypeDataWithSecurityData[];
      };
    };
  };
}

export interface AnalyticsPortfolioHoldingsData {
  portfolio: {
    id: number;
  };
  firstAnalysis: {
    marketValue: number;
    tradeAmount: number;
  } | null;
  securityTypes: AnalyticsSecurityTypeData[];
}

export interface PortfolioHoldingsFromAnalyticsQuery {
  analytics: {
    grouppedAnalytics: AnalyticsPortfolioHoldingsData;
  };
}

export enum ProfileClassificationCode {
  RET = "RET", // Retail
  NPROF = "NPROF", // Non-Professional
  PROF = "PROF", // Professional
  CP = "CP", //Eligible Counterparty
  NA = "NA", // Not Available
}

export enum SecurityTag {
  UNSA_YES = "UNSA-Yes",
  NOOF_YES = "NOOF-Yes",
  PRICE_EXPIRED = "Price-Expired",
  PREMATURITY_EXPIRED = "Prematurity-Expired",
  TRADEABLE_BACK_ONLY = "TradeableBackOnly",
  SUBSCR_PORTAL_GROUP = "SubscrPortalGroup",
  SUBSCR_COUNTRY_LV_ONLY = "SubscrCountry:LV_only",
  SUBSCR_COUNTRY_LT_ONLY = "SubscrCountry:LT_only",
}


